import { DiscoveryStatusBadge } from '@/components/discovery/DiscoveryStatusBadge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/hooks/useAuth';
import { useDatasetsList } from '@/hooks/useDatasets';
import { useDiscoveriesList } from '@/hooks/useDiscoveries';
import { Database, Network, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

export default function Dashboard() {
  const { org, quota } = useAuth();
  const datasetsQuery = useDatasetsList({ limit: 1 });
  // Active polling so a freshly-submitted "queued" run flips to "succeeded"
  // without the user mashing F5. Polling stops on its own once every visible
  // item is terminal (see useDiscoveriesList).
  const discoveriesQuery = useDiscoveriesList({ limit: 5, enableActivePolling: true });

  const periodUsed = quota?.used_this_period ?? 0;
  const periodCap = quota?.period_cap ?? null;
  const lifetimeUsed = quota?.lifetime_used ?? 0;
  const lifetimeCap = quota?.lifetime_cap ?? null;
  const plan = org?.plan ?? 'free';

  // For Free plan only `lifetime_cap` matters (no monthly reset). Paid
  // plans use `period_cap`. Render whichever has a finite cap, then fall
  // back to "—".
  const quotaTitle = (() => {
    if (periodCap != null) return `${periodUsed} / ${periodCap}`;
    if (lifetimeCap != null) return `${lifetimeUsed} / ${lifetimeCap}`;
    return String(lifetimeUsed);
  })();

  const quotaSub = (() => {
    if (periodCap != null && quota?.period_end) {
      return `Resets ${new Date(quota.period_end).toLocaleDateString()}`;
    }
    if (lifetimeCap != null) return 'Free plan · lifetime';
    return 'Unlimited';
  })();

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
          <p className="text-sm text-muted-foreground">
            Welcome to DAGsight — {plan.charAt(0).toUpperCase() + plan.slice(1)} plan.
          </p>
        </div>
        <Button asChild>
          <Link to="/discoveries/new">
            <Plus className="mr-1 h-4 w-4" /> New discovery
          </Link>
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardDescription>Discoveries used</CardDescription>
            <CardTitle className="text-3xl tabular-nums">{quotaTitle}</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">{quotaSub}</CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardDescription>Datasets</CardDescription>
            <CardTitle className="text-3xl tabular-nums">
              {datasetsQuery.isLoading ? (
                <Skeleton className="inline-block h-7 w-12" />
              ) : (
                (datasetsQuery.data?.items.length ?? 0)
              )}
              {datasetsQuery.data?.next_cursor ? '+' : ''}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            <Link to="/datasets" className="hover:underline">
              Manage
            </Link>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardDescription>Plan</CardDescription>
            <CardTitle className="text-3xl capitalize">{plan}</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            <Link to="/billing" className="hover:underline">
              Upgrade
            </Link>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-base">Recent discoveries</CardTitle>
            <CardDescription>Last 5 runs in this workspace.</CardDescription>
          </div>
          <Button asChild variant="ghost" size="sm">
            <Link to="/discoveries">View all</Link>
          </Button>
        </CardHeader>
        <CardContent>
          {discoveriesQuery.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-10" />
              <Skeleton className="h-10" />
              <Skeleton className="h-10" />
            </div>
          ) : discoveriesQuery.data?.items.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-md border border-dashed py-10 text-center">
              <Network className="mb-2 h-8 w-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">No discoveries yet.</p>
            </div>
          ) : (
            <div className="divide-y">
              {discoveriesQuery.data?.items.map((d) => (
                <Link
                  key={d.id}
                  to={`/discoveries/${d.id}`}
                  className="flex items-center justify-between py-2 hover:bg-muted/30"
                >
                  <div className="flex items-center gap-3">
                    <DiscoveryStatusBadge status={d.status} />
                    <span className="font-mono text-xs">{d.id}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">{fmtDate(d.created_at)}</span>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {datasetsQuery.data?.items.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Get started</CardTitle>
            <CardDescription>Upload a numeric CSV to run your first discovery.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            <Button asChild>
              <Link to="/datasets">
                <Database className="mr-1 h-4 w-4" /> Upload a dataset
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/marketing-attribution" target="_blank" rel="noopener noreferrer">
                See the marketing-attribution demo
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
